import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { AuthContext } from './AuthContext';
import { CourseIDContext } from './CourseIDContext';


const StudentAnswerContext = createContext();

const StudentAnswerProvider = ({ children }) => {
  const [studentAnswersState, setStudentAnswersState] = useState({});
  const [pendingChanges, setPendingChanges] = useState([]);
  
  // Add save status state
  const [saveStatus, setSaveStatus] = useState({
    status: 'saved', // Start with 'saved' as default state (always show checkmark)
    lastSaved: null,
    pendingCount: 0,
    errorMessage: null,
    onRetry: null
  });

const { courseID } = useContext(CourseIDContext);

  // Add a ref to keep track of the latest pendingChanges in debounced functions
  const pendingChangesRef = useRef(pendingChanges);

  // Update the ref whenever pendingChanges changes
  useEffect(() => {
    pendingChangesRef.current = pendingChanges;
    // Only update status if not in error state
    if (saveStatus.status !== 'error') {
      if (pendingChanges.length > 0) {
        setSaveStatus(prev => ({
          ...prev,
          status: 'saving', // Show saving while there are pending changes
          pendingCount: pendingChanges.length
        }));
      } else if (pendingChanges.length === 0 && saveStatus.status === 'saving') {
        // When pending changes clear, show saved briefly then go to default 'saved' state
        setSaveStatus(prev => ({
          ...prev,
          status: 'saved',
          pendingCount: 0
        }));
      }
    }
  }, [pendingChanges, saveStatus.status]);
  
  const apiUrl = process.env.REACT_APP_API_BASE_URL;

 const authContext = useContext(AuthContext);  // Use AuthContext here


// Fetch initial state from the backend on page load and when the tab regains focus

  const lastBlurTimeRef = useRef(null);


useEffect(() => {
  if (!authContext.isLoggedIn) return; // no token yet — don't fire a pre-login request
  const TEN_MINUTES = 10 * 60 * 1000; // 10 minutes in milliseconds

  const fetchData = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/studentAnswers/${courseID}/${authContext.username}`);

      // Normalize usernames (trim + lowercase) when grouping/matching so that
      // stray whitespace or case differences in stored data don't cause a
      // student's answers to be dropped on load.
      const normalizeUsername = (u) => (u || '').trim().toLowerCase();
      const myUsername = normalizeUsername(authContext.username);

      const initialState = response.data.reduce((acc, item) => {
        const key = normalizeUsername(item.username);
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push({
          questionID: item.questionID.toString(),
          questionAnswer: item.questionAnswer?.toString(),
          questionEssayAnswer: item.questionEssayAnswer?.toString(),
        });
        return acc;
      }, {});

      if (initialState[myUsername] && initialState[myUsername].length > 0) {
        setStudentAnswersState({ [myUsername]: initialState[myUsername] });
      } else {
        console.warn('API returned empty or invalid data for user:', myUsername);
      }

    } catch (error) {
      console.error('Error fetching student answer data:', error);
    }
  };

  fetchData(); // initial fetch on page load stays the same!

  // These handlers are your ONLY addition.
  const handleBlur = () => {
    lastBlurTimeRef.current = Date.now();
  };

  const handleFocus = () => {
    const now = Date.now();
    if (lastBlurTimeRef.current && now - lastBlurTimeRef.current > TEN_MINUTES) {
      //console.log("Tab inactive >10 min, re-fetching student answers...");
      fetchData();
    } else {
      //console.log("Tab inactive <10 min, no fetch triggered.");
    }
    lastBlurTimeRef.current = null;
  };

  window.addEventListener("blur", handleBlur);
  window.addEventListener("focus", handleFocus);
  
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") handleBlur();
    if (document.visibilityState === "visible") handleFocus();
  });

  return () => {
    window.removeEventListener("blur", handleBlur);
    window.removeEventListener("focus", handleFocus);
    document.removeEventListener("visibilitychange", handleBlur);
    document.removeEventListener("visibilitychange", handleFocus);
  };
}, [apiUrl, authContext.isLoggedIn, authContext.username, courseID]);

  

  // Debounced function to update the database
 const updateDatabase = useCallback(() => {
  const changesToSend = pendingChangesRef.current;
  if (changesToSend.length === 0) {
    setSaveStatus(prev => ({ ...prev, status: 'saved' }));
    return;
  }

  // Keep status as 'saving' - it should already be set from the useEffect
  setSaveStatus(prev => ({ 
    ...prev, 
    status: 'saving',
    errorMessage: null 
  }));

  axios.post(`${apiUrl}/api/studentAnswers/update/${courseID}`, { answerData: changesToSend })
    .then(response => {
      if (response.data.historyErrors) {
        console.warn('Some history updates failed:', response.data.historyErrors);
      }
      
      // Clear pendingChanges after successful update
      setPendingChanges((prevPendingChanges) => prevPendingChanges.filter(
        (change) => !changesToSend.some(
          (sentChange) => sentChange.username === change.username && sentChange.questionID === change.questionID
        )
      ));
      
      // Set status to saved - this will be the persistent state
      setSaveStatus(prev => ({
        ...prev,
        status: pendingChangesRef.current.length > 0 ? 'saving' : 'saved',
        lastSaved: new Date(),
        errorMessage: null
      }));
    })
    .catch(error => {
      console.error('Error updating student answer data:', error);
      
      // Set status to error with retry function
      setSaveStatus(prev => ({
        ...prev,
        status: 'error',
        errorMessage: 'Failed to save. Please check your connection.',
        onRetry: () => updateDatabase()
      }));
    });
}, [apiUrl, courseID]);



const debouncedUpdateDatabase = useCallback(
  debounce(() => {
    updateDatabase();
  }, 500),
  [updateDatabase]
);

  // Function to handle changes in the state and database
 const handleAnswerChange = (username, questionID, answer, isEssay = false) => {
  setStudentAnswersState((prevState) => {
    const userAnswers = prevState[username] || [];
    const existingEntryIndex = userAnswers.findIndex(item => item.questionID === questionID);

    let updatedUserAnswers;

    if (existingEntryIndex !== -1) {
      // Update existing answer
      const existingItem = userAnswers[existingEntryIndex];
      const updatedItem = isEssay 
        ? { ...existingItem, questionEssayAnswer: answer }
        : { ...existingItem, questionAnswer: answer };

      // Check if the answer has actually changed
      if (JSON.stringify(existingItem) === JSON.stringify(updatedItem)) {
        console.log('No change detected, returning early.');
        return prevState;
      }

      updatedUserAnswers = [
        ...userAnswers.slice(0, existingEntryIndex),
        updatedItem,
        ...userAnswers.slice(existingEntryIndex + 1)
      ];
    } else {
      // Add new answer
      const newEntry = {
        questionID,
        questionAnswer: isEssay ? null : answer,
        questionEssayAnswer: isEssay ? answer : null,
      };
      updatedUserAnswers = [...userAnswers, newEntry];
    }

    const newState = {
      ...prevState,
      [username]: updatedUserAnswers
    };

    // Update pendingChanges
    setPendingChanges((prevPendingChanges) => {
      const existingPendingIndex = prevPendingChanges.findIndex(
        (item) => item.username === username && item.questionID === questionID
      );
      const changedItem = {
        username,
        questionID,
        questionAnswer: isEssay ? null : answer,
        questionEssayAnswer: isEssay ? answer : null,
      };
      if (existingPendingIndex !== -1) {
        // Update existing pending change
        const newPendingChanges = [...prevPendingChanges];
        newPendingChanges[existingPendingIndex] = changedItem;
        return newPendingChanges;
      } else {
        // Add new pending change
        return [...prevPendingChanges, changedItem];
      }
    });

    // Call debouncedUpdateDatabase
    debouncedUpdateDatabase();

    return newState;
  });
};


  return (
    <StudentAnswerContext.Provider value={{ 
      studentAnswersState, 
      handleAnswerChange,
      saveStatus
    }}>
      {children}
    </StudentAnswerContext.Provider>
  );
};

export { StudentAnswerContext, StudentAnswerProvider };